import React, { ReactNode } from 'react';
import { css } from '@emotion/css';
import { StatusMessage } from 'is24-corecss';

import Notifications from './Notifications';
import { List } from './List';
import { OverviewFooter } from './OverviewFooter';
import { OverviewDescription } from './OverviewDescription';
import { BILLCHECK_V3_STATUS, BillcheckV3 } from '@/@types/billCheckV3';
import { useIntl } from '@/i18n/hooks/useIntl';
import { default as Msg } from '@/i18n/components/FormattedMessage';

const styles = {
  statusMessageParagraph: css`
    margin-top: 8px;
  `,
  statusMessageOrdered: { paddingTop: '12px' },
  statusMessageCta: { marginTop: '12px' },
};

export const BillCheckV3OverviewTab = ({
  isEntitled,
  lastBillCheck,
}: {
  isEntitled: boolean;
  lastBillCheck: BillcheckV3;
}) => {
  const { t } = useIntl();

  const pageStatusState = {
    [BILLCHECK_V3_STATUS.PROCESSING]: (
      <>
        <StatusMessage
          statusType='confirm'
          headline={t({
            id: `benefits.billCheckV3.modal.overview.statusBox.submitted.headline`,
            defaultMessage: 'Die Prüfung deiner Unterlagen hat begonnen',
          })}
          message={
            <>
              <p className={styles.statusMessageParagraph}>
                {t({
                  id: `benefits.billCheckV3.modal.overview.statusBox.submitted.description`,
                  defaultMessage: 'Innerhalb von 5 Werktagen erhältst du das Ergebnis per E-Mail.',
                })}
              </p>
              <Msg
                id='benefits.billCheckV3.modal.overview.statusBox.ticketNumberInfoLabel'
                defaultMessage='Deine Auftragsnummer: '
                values={{
                  b: (children: ReactNode) => <b>{children}</b>,
                }}
              />
              {lastBillCheck.ticketId}
            </>
          }
        />
        <List />
        <OverviewFooter />
      </>
    ),
    [BILLCHECK_V3_STATUS.USER_ACTION]: (
      <>
        <Notifications />
        <List />
        <OverviewFooter />
      </>
    ),
    [BILLCHECK_V3_STATUS.FINISHED]: (
      <>
        <StatusMessage
          statusType='confirm'
          headline={t({
            id: `benefits.billCheckV3.modal.overview.statusBox.done.headline`,
            defaultMessage: 'Die Prüfung deiner Unterlagen wurde abgeschlossen',
          })}
          message={
            <>
              <p className={styles.statusMessageParagraph}>
                {t({
                  id: `benefits.billCheckV3.modal.overview.statusBox.done.description`,
                  defaultMessage: 'Wir haben dir das Ergebnis der Prüfung per E-Mail zugesendet.',
                })}
              </p>
              <Msg
                id='benefits.billCheckV3.modal.overview.statusBox.ticketNumberInfo'
                defaultMessage='Deine Auftragsnummer: {minekoTicketId}'
                values={{ minekoTicketId: lastBillCheck.ticketId, b: (children: ReactNode) => <b>{children}</b> }}
              />
            </>
          }
        />
        <List />
        <OverviewFooter />
      </>
    ),
  };

  return (
    pageStatusState?.[lastBillCheck.billCheckStatus as keyof typeof pageStatusState] || (
      <>
        <OverviewDescription isEntitled={isEntitled} />
        <List />
      </>
    )
  );
};
